import { DownloadOutlined, HomeOutlined, LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link, request as umiRequest, useLocation } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Button, Dropdown, Modal, message } from 'antd';
import React from 'react';
import {
  AiAgentIcon,
  AvatarDropdown,
  AvatarName,
  GrokIcon,
  Sora2Icon,
  ThemeSunIcon,
} from '@/components';
import { FinanceLogsModalTrigger } from '@/components/FinanceLogsModal';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { clearAuthToken, getAuthToken } from './authToken';
import {
  applyThemeToDom,
  getStoredThemeMode,
  getThemeSettings,
  setStoredThemeMode,
  type AppThemeMode,
} from './theme';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
const desktopAuthPath = '/desktop-auth';
const APP_MODE_KEY = 'qn_app_mode';
type AppMode = 'agent' | 'geo';
const getStoredAppMode = (): AppMode => {
  if (typeof localStorage === 'undefined') return 'geo';
  const v = localStorage.getItem(APP_MODE_KEY);
  if (v === 'agent') return 'agent';
  return 'geo';
};
const setStoredAppMode = (mode: AppMode) => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(APP_MODE_KEY, mode);
};
const isGeoPath = (pathname?: string) => String(pathname || '').startsWith('/geo');

type ApiResponse<T> = {
  success: boolean;
  data: T;
  errorCode?: string | null;
  errorMessage?: string | null;
};

type AuthClientReleaseLatestVO = {
  recordId: number;
  platform: string;
  arch?: string;
  version: string;
  downloadUrl: string;
  uploadedAt?: string | null;
};

const detectAuthClientPlatform = (): 'win' | 'mac' => {
  const ua = String(navigator.userAgent || '').toLowerCase();
  const pf = String(navigator.platform || '').toLowerCase();
  const v = `${ua} ${pf}`;
  if (v.includes('windows')) return 'win';
  if (v.includes('mac') || v.includes('os x') || v.includes('iphone') || v.includes('ipad')) return 'mac';
  return 'win';
};

const fetchLatestAuthClient = async (platform: 'win' | 'mac', arch?: 'arm64' | 'x64') => {
  try {
    const resp = await umiRequest<ApiResponse<AuthClientReleaseLatestVO>>('/api/auth-client/releases/latest', {
      method: 'GET',
      params: { platform, arch },
    } as any);
    // 期望后端返回：{ success: boolean, data: { downloadUrl, ... } }
    const anyResp = resp as any;
    const success = typeof anyResp?.success === 'boolean' ? anyResp.success : anyResp?.data?.success;
    const url = anyResp?.data?.downloadUrl ?? anyResp?.data?.data?.downloadUrl;
    if (!success || !url) {
      message.warning('暂无可下载的最新版安装包');
      return null;
    }
    return url as string;
  } catch {
    message.error('获取安装包失败');
    return null;
  }
};

const downloadLatestAuthClient = async () => {
  const platform = detectAuthClientPlatform();
  if (platform === 'win') {
    const url = await fetchLatestAuthClient('win', 'x64');
    if (url) window.location.href = url;
    return;
  }
  Modal.confirm({
    title: '下载桌面端（Mac）',
    content: '请选择适配你的 Mac 架构的安装包：Apple Silicon(arm64) 或 Intel(x64)。',
    okText: 'Apple Silicon (arm64)',
    cancelText: '取消',
    maskClosable: true,
    keyboard: true,
    onOk: async () => {
      const url = await fetchLatestAuthClient('mac', 'arm64');
      if (url) window.location.href = url;
    },
  });
};

const applyAppModeToDom = (mode: AppMode) => {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.app = mode;
};
const filterMenuByMode = (items: any[], mode: AppMode): any[] => {
  const keepGeo = mode === 'geo';
  const walk = (arr: any[]): any[] =>
    (arr || [])
      .map((it) => {
        const children = Array.isArray(it.children) ? walk(it.children) : it.children;
        return { ...it, children };
      })
      .filter((it) => {
        const p = String(it.path || '');
        // layout=false 的 /user/*、* 不会进菜单；这里只处理业务菜单
        const geo = p.startsWith('/geo');
        return keepGeo ? geo : !geo;
      });
  return walk(items);
};

const applyAppModeAndNavigate = (next: AppMode) => {
  setStoredAppMode(next);
  applyAppModeToDom(next);
  const to = next === 'geo' ? '/geo/home' : '/home';
  if (history.location.pathname !== to) {
    history.push(to);
  }
};

const THEME_SWITCH_ITEMS: { key: AppThemeMode; label: string }[] = [
  { key: 'light-tech', label: '浅色科技' },
  { key: 'dark-tech', label: '深色科技' },
];

/** 顶栏主题：太阳图标 +「主题切换」文案，下拉三项（与设计稿一致） */
const ThemeSwitchDropdown: React.FC<{
  setInitialState: React.Dispatch<React.SetStateAction<any>>;
}> = ({ setInitialState }) => {
  const [mode, setMode] = React.useState<AppThemeMode>(() => getStoredThemeMode());
  const menu: MenuProps = {
    selectable: true,
    selectedKeys: [mode],
    items: THEME_SWITCH_ITEMS.map(({ key, label }) => ({ key, label })),
    onClick: ({ key }) => {
      const m = key as AppThemeMode;
      setStoredThemeMode(m);
      applyThemeToDom(m);
      setMode(m);
      setInitialState((pre: any) => ({
        ...pre,
        settings: {
          ...(pre?.settings || {}),
          ...getThemeSettings(m),
        },
      }));
    },
  };
  return (
    <Dropdown
      menu={menu}
      placement="bottomLeft"
      trigger={['click']}
      overlayClassName="qn-theme-dropdown"
      arrow
      onOpenChange={(open) => {
        if (open) setMode(getStoredThemeMode());
      }}
    >
      <button type="button" className="qn-theme-switch-trigger">
        <ThemeSunIcon className="qn-theme-switch-sun" />
        <span>主题切换</span>
      </button>
    </Dropdown>
  );
};

/** 顶栏左侧 GEO / Agent 分开展示，需随路由刷新选中态 */
const AppModeSwitch: React.FC = () => {
  useLocation();
  const currentAppMode = getStoredAppMode();
  return (
    <div className="qn-app-mode-switch">
      <button
        type="button"
        className={`qn-app-mode-btn${currentAppMode === 'geo' ? ' qn-app-mode-btn-active' : ''}`}
        onClick={() => applyAppModeAndNavigate('geo')}
      >
        <HomeOutlined className="qn-app-mode-icon" />
        GEO营销中心
      </button>
      <button
        type="button"
        className={`qn-app-mode-btn${currentAppMode === 'agent' ? ' qn-app-mode-btn-active' : ''}`}
        onClick={() => applyAppModeAndNavigate('agent')}
      >
        <AiAgentIcon className="qn-app-mode-icon qn-app-mode-icon--agent" />
        Ai agent
      </button>
    </div>
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result', desktopAuthPath].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    const mode = getStoredThemeMode();
    // 初始化时同步到 DOM（用于 CSS 变量主题）
    if (typeof document !== 'undefined') applyThemeToDom(mode);
    return {
      fetchUserInfo,
      currentUser,
      settings: {
        ...(defaultSettings as Partial<LayoutSettings>),
        ...getThemeSettings(mode),
      },
    };
  }
  const mode = getStoredThemeMode();
  if (typeof document !== 'undefined') applyThemeToDom(mode);
  return {
    fetchUserInfo,
    settings: {
      ...(defaultSettings as Partial<LayoutSettings>),
      ...getThemeSettings(mode),
    },
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const currentAppMode = getStoredAppMode();
  const balance = Number((initialState?.currentUser as any)?.meijieheziBalance ?? 0);
  // 初始化时把 AppMode 同步到 DOM（用于 GEO 专属主题）
  applyAppModeToDom(currentAppMode);

  return {
    // 菜单直接使用 routes.ts 的 name（不走 i18n key），避免出现 menu.xxx 缺失提示
    menu: { locale: false },
    // 顶栏右侧：同一「盒子」内左侧 GEO/Agent，右侧与头像对称（积分、主题、下载成组靠右）
    actionsRender: () => [
      <div key="header-actions-split" className="qn-header-actions-split">
        <div className="qn-header-actions-split-left">
          <AppModeSwitch />
        </div>
        <div className="qn-header-actions-split-right">
          <FinanceLogsModalTrigger balance={balance} />
          <ThemeSwitchDropdown setInitialState={setInitialState} />
          <Button type="text" icon={<DownloadOutlined />} onClick={() => void downloadLatestAuthClient()}>
            下载
          </Button>
        </div>
      </div>,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: false,
    onPageChange: () => {
      const { location } = history;
      const appMode = getStoredAppMode();
      applyAppModeToDom(appMode);
      // 如果没有登录，重定向到 login
      if (
        !initialState?.currentUser &&
        location.pathname !== loginPath &&
        location.pathname !== desktopAuthPath
      ) {
        history.push(loginPath);
        return;
      }
      // Agent/GEO 模式下，强制停留在各自的路由空间
      if (location.pathname !== loginPath) {
        if (appMode === 'geo' && !isGeoPath(location.pathname)) {
          history.push('/geo/home');
          return;
        }
        if (appMode === 'agent' && isGeoPath(location.pathname)) {
          history.push('/home');
          return;
        }
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    menuDataRender: (menuData) => {
      const appMode = getStoredAppMode();
      const patch = (items: any[]): any[] =>
        items.map((it) => {
          const children = Array.isArray(it.children) ? patch(it.children) : it.children;
          if (it.path === '/grok-video') {
            return { ...it, icon: <GrokIcon />, children };
          }
          if (it.path === '/sora2-video') {
            return { ...it, icon: <Sora2Icon />, children };
          }
          return { ...it, children };
        });
      return filterMenuByMode(patch(menuData as any), appMode);
    },
    // 不需要每个页面上方的 Ant PageHeader 标题栏
    pageHeaderRender: false,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...errorConfig,
  requestInterceptors: [
    (url, options) => {
      const token = getAuthToken();
      if (token) {
        const headers = {
          ...(options.headers || {}),
          Authorization: `Bearer ${token}`,
        };
        return {
          url,
          options: {
            ...options,
            headers,
          },
        };
      }
      return { url, options };
    },
  ],
  responseInterceptors: [
    async (response) => {
      if (response.status === 401) {
        clearAuthToken();
        if (history.location.pathname !== loginPath) {
          history.push(loginPath);
        }
      }
      return response;
    },
  ],
};
